// XOnb: one onboarding question (O2-O5): back + front-loaded progress bar, title, options (2-column grid or list),
// optional voice slot and footnote, and "Next" (disabled until answered; founder 11-01: no Skip).
// Presentational: the screen owns the answer (screens/onboarding/O2_O5.tsx).
import type { ComponentChildren } from 'preact';
import { Icon, type IconName } from './icons';
import { OnbTopBar } from './OnbTopBar';
import { PrimaryButton } from './buttons';
import { ScrollArea } from './ScrollArea';
import s from './XOnb.module.css';

export interface XOnbOption {
  key: string;
  label: string;
  sub?: string;
  /** Small caps tag next to the label (O5 "MOST PEOPLE"). */
  tag?: string;
  icon?: IconName;
}

export interface XOnbProps {
  title: string;
  sub?: string;
  layout: 'grid' | 'list';
  options: XOnbOption[];
  /** Selected option keys. */
  selected: string[];
  onPick: (key: string) => void;
  /** Option min-height at the 844px design (grid 112, tone 132, list 76, check-ins 92). Shrinks on short screens. */
  optionHeight: number;
  /** Subs are example lines: italic, lighter (O4). */
  exampleSubs?: boolean;
  pct: number;
  onBack: () => void;
  onNext: () => void;
  canNext: boolean;
  /** Footnote above Next (O5). */
  foot?: string;
  /** Slot under the options (O2 "Or say it in your own words"). */
  voice?: ComponentChildren;
}

export function XOnb(p: XOnbProps) {
  const h = p.optionHeight;
  const minH = `clamp(${Math.round(h * 0.72)}px, ${(h / 8.44).toFixed(2)}vh, ${h}px)`;
  return (
    <>
      <div class={s.glow} aria-hidden="true" />
      <OnbTopBar pct={p.pct} onBack={p.onBack} />
      <ScrollArea class={s.body} fadeTop={16}>
        <div class={s.head} style={{ marginBottom: p.sub ? 18 : 25 }}>
          <h1 class={s.title}>{p.title}</h1>
          {p.sub && <p class={s.sub}>{p.sub}</p>}
        </div>
        <div class={s.options} role="group" aria-label={p.title} data-layout={p.layout}>
          {p.options.map((o) => {
            const on = p.selected.includes(o.key);
            if (p.layout === 'grid') {
              return (
                <button key={o.key} type="button" class={s.cell} data-on={on ? 'true' : 'false'} aria-pressed={on ? 'true' : 'false'} style={{ minHeight: minH }} onClick={() => p.onPick(o.key)}>
                  {o.icon && <Icon name={o.icon} size={26} strokeWidth={1.8} color="var(--acc)" />}
                  <span class={s.cellLabel}>{o.label}</span>
                  {o.sub && <span class={p.exampleSubs ? s.example : s.cellSub}>{p.exampleSubs ? `"${o.sub}"` : o.sub}</span>}
                  {on && <span class={s.check} aria-hidden="true"><Icon name="check" size={14} strokeWidth={3} color="var(--on)" /></span>}
                </button>
              );
            }
            return (
              <button key={o.key} type="button" class={s.row} data-on={on ? 'true' : 'false'} aria-pressed={on ? 'true' : 'false'} style={{ minHeight: minH }} onClick={() => p.onPick(o.key)}>
                {o.icon && <Icon name={o.icon} size={26} strokeWidth={1.8} color="var(--acc)" />}
                <span class={s.rowText}>
                  <span class={s.rowTop}><span class={s.rowLabel}>{o.label}</span>{o.tag && <span class={s.tag}>{o.tag}</span>}</span>
                  {o.sub && <span class={s.rowSub}>{o.sub}</span>}
                </span>
                <span class={s.radio} data-on={on ? 'true' : 'false'} aria-hidden="true"><Icon name="check" size={14} strokeWidth={3} color={on ? 'var(--on)' : 'transparent'} /></span>
              </button>
            );
          })}
        </div>
        {p.voice}
        <div class={s.spacer} />
        {p.foot && <p class={s.foot}>{p.foot}</p>}
      </ScrollArea>
      <div class={s.next}>
        <PrimaryButton disabled={!p.canNext} onClick={p.onNext} data-cta="next">Next</PrimaryButton>
      </div>
    </>
  );
}
